import React, { useState, useEffect, useRef } from "react";
import "./Compliance.css";
import { useNavigate } from 'react-router-dom';
import ComplainceHero from "./ComplainceHero";
import ComplianceWiseCode from "./ComplainceWiseCode";
import HRCopilotNewsSection from "./HRCopilotNewsSection";
import HRCopilotFeatures from './HRCopilotFeatures';


const VideoPopup = ({ isOpen, onClose, videoUrl }) => {
  const videoRef = useRef(null);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      if (videoRef.current) {
        videoRef.current.currentTime = 0;
      }
    } else {
      document.body.style.overflow = 'unset';
    }

    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="compliance-video-popup-overlay" onClick={onClose}>
      <div className="compliance-video-popup-content" onClick={(e) => e.stopPropagation()}>
        <button className="compliance-video-popup-close" onClick={onClose}>
          ×
        </button>
        <div className="compliance-video-container">
          <video ref={videoRef} controls autoPlay className="compliance-demo-video">
            <source src={videoUrl} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
        <div className="compliance-video-popup-info">
          <h3>Compliance Management Platform</h3>
          <p>See how our compliance platform automates regulatory tracking and risk management</p>
        </div>
      </div>
    </div>
  );
};

const Compliance = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isVideoPopupOpen, setIsVideoPopupOpen] = useState(false);
  const [activeFeature, setActiveFeature] = useState(0);
  const [scrollProgress, setScrollProgress] = useState(0);
  const [activeTab, setActiveTab] = useState("regulatory");
  const navigate = useNavigate();
    const videoRef = useRef(null);
    const [scrollY, setScrollY] = useState(0);

      const handleRecruit = () => navigate('/access-recruit');

  useEffect(() => {
    setIsVisible(true);
    
    // Auto rotate features
    const interval = setInterval(() => {
      setActiveFeature(prev => (prev + 1) % 6);
    }, 4000);
    
    // Scroll progress
    const handleScroll = () => {
      const scrollTop = window.pageYOffset;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (scrollTop / docHeight) * 100;
      setScrollProgress(progress);
    };

    window.addEventListener('scroll', handleScroll);
    
    return () => {
      clearInterval(interval);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const demoVideoUrl = "https://assets.mixkit.co/videos/preview/mixkit-businessman-analyzing-data-on-a-computer-44539-large.mp4";

  const openVideoPopup = () => {
    setIsVideoPopupOpen(true);
  };

  const closeVideoPopup = () => {
    setIsVideoPopupOpen(false);
  };

  const features = [
    {
      icon: "⚖️",
      title: "Regulatory Tracking",
      description: "Automated monitoring of labor laws, industry regulations, and compliance requirements across jurisdictions"
    },
    {
      icon: "📊",
      title: "Audit Management",
      description: "Complete audit trail, documentation, and reporting for internal and external compliance audits"
    },
    {
      icon: "🔔",
      title: "Automated Alerts",
      description: "Real-time notifications for compliance deadlines, regulation changes, and policy violations"
    },
    {
      icon: "📝",
      title: "Policy Management",
      description: "Centralized policy creation, distribution, and acknowledgment tracking across your organization"
    },
    {
      icon: "🛡️",
      title: "Risk Assessment",
      description: "Proactive risk identification and mitigation strategies with compliance scoring and monitoring"
    },
    {
      icon: "📈",
      title: "Compliance Analytics",
      description: "Advanced reporting and analytics to track compliance performance and identify improvement areas"
    }
  ];

const complianceStages = [
  {
    phase: "Assessment",
    icon: "📋",
    time: "1-2 days",
    title: "Risk Assessment & Gap Analysis",
    steps: [
      "Current compliance state audit",
      "Regulatory requirement mapping",
      "Risk exposure analysis",
      "Compliance gap identification"
    ]
  },
  {
    phase: "Design",
    icon: "🎨",
    time: "3-5 days",
    title: "Compliance Framework Design",
    steps: [
      "Policy & procedure development",
      "Control framework implementation",
      "Monitoring system configuration",
      "Reporting dashboard setup"
    ]
  },
  {
    phase: "Implementation",
    icon: "⚡",
    time: "2-3 weeks",
    title: "System Implementation & Integration",
    steps: [
      "Platform configuration & setup",
      "Integration with existing systems",
      "Automated monitoring activation",
      "Team training & onboarding"
    ]
  },
  {
    phase: "Management",
    icon: "🔄",
    time: "Continuous",
    title: "Ongoing Monitoring & Optimization",
    steps: [
      "Real-time regulatory updates",
      "Automated compliance tracking",
      "Risk mitigation automation",
      "Continuous improvement cycles"
    ]
  }
];

const complianceMetrics = [
  { name: "Policy Compliance", progress: 96 },
  { name: "Regulatory Updates", progress: 99 },
  { name: "Risk Mitigation", progress: 92 },
  { name: "Audit Readiness", progress: 98 }
];

const complianceIntegrations = [
  "HR Systems", "Legal Software", "Document Management", "Risk Platforms", 
  "Audit Tools", "Governance Systems", "ERP Systems", "CRM Platforms", 
  "Microsoft 365", "Google Workspace", "Slack", "Teams"
];

const complianceTabs = [
  { id: 'monitoring', label: 'Monitoring' },
  { id: 'risk', label: 'Risk Management' },
  { id: 'audit', label: 'Audit & Reporting' },
  { id: 'training', label: 'Training' }
];

const handleAccessCompliance = () => {
  // Handle compliance management access
  window.open('/compliance-platform', '_blank');
  console.log("Access compliance management");
};

const openComplianceVideoPopup = () => {
  // Open compliance management video popup
  window.open('/compliance-demo', '_blank');
  console.log("Open compliance management video");
};

const handleExploreCompliance = () => {
  // Handle explore compliance integrations
  window.open('/compliance-integrations', '_blank');
  console.log("Explore compliance integrations");
};

  const tabs = [
    {
      id: "regulatory",
      label: "REGULATORY COMPLIANCE",
      content: {
        icon: "⚖️",
        title: "Automated Regulatory Tracking",
        description: "Stay ahead of changing labor laws and industry regulations with automated monitoring and compliance verification across all jurisdictions.",
        features: ["LABOR LAW COMPLIANCE", "INDUSTRY REGULATIONS", "MULTI-JURISDICTION", "REAL-TIME UPDATES"],
        secondary: {
          icon: "🔔",
          title: "Automated Compliance Alerts",
          description: "Receive instant notifications for regulation changes, compliance deadlines, and potential violations before they become issues."
        },
        buttonText: "EXPLORE REGULATORY TOOLS"
      }
    },
    {
      id: "audit",
      label: "AUDIT MANAGEMENT →",
      content: {
        icon: "📋",
        title: "Comprehensive Audit Readiness",
        description: "Maintain complete audit trails, generate compliance reports, and streamline internal and external audit processes.",
        features: ["AUDIT TRAILS", "DOCUMENTATION", "REPORT GENERATION", "COMPLIANCE CERTIFICATION"],
        secondary: {
          icon: "📊",
          title: "Audit Analytics & Insights",
          description: "Identify compliance trends, track audit findings, and implement corrective actions with detailed analytics."
        },
        buttonText: "EXPLORE AUDIT TOOLS"
      }
    },
    {
      id: "policy",
      label: "POLICY MANAGEMENT",
      content: {
        icon: "📝",
        title: "Centralized Policy Control",
        description: "Create, distribute, and track policy acknowledgments across your organization with automated reminders and compliance tracking.",
        features: ["POLICY CREATION", "EMPLOYEE ACKNOWLEDGMENT", "VERSION CONTROL", "COMPLIANCE TRACKING"],
        secondary: {
          icon: "✅",
          title: "Policy Compliance Monitoring",
          description: "Monitor policy adherence, track completion rates, and ensure regulatory compliance across all departments."
        },
        buttonText: "EXPLORE POLICY TOOLS"
      }
    },
    {
      id: "risk",
      label: "RISK MANAGEMENT",
      content: {
        icon: "🛡️",
        title: "Proactive Risk Mitigation",
        description: "Identify, assess, and mitigate compliance risks with advanced analytics and automated monitoring systems.",
        features: ["RISK ASSESSMENT", "MITIGATION STRATEGIES", "COMPLIANCE SCORING", "RISK MONITORING"],
        secondary: {
          icon: "📈",
          title: "Risk Analytics & Reporting",
          description: "Track risk metrics, generate compliance reports, and make data-driven decisions to minimize organizational risk."
        },
        buttonText: "EXPLORE RISK TOOLS"
      }
    }
  ];

  const activeTabContent = tabs.find(tab => tab.id === activeTab)?.content;

  return (
    <div className="compliance-container">
      {/* Scroll Progress Bar */}
      {/* <div className="compliance-scroll-progress" style={{width: `${scrollProgress}%`}}></div> */}

      {/* Video Popup */}
      <VideoPopup 
        isOpen={isVideoPopupOpen} 
        onClose={closeVideoPopup} 
        videoUrl={demoVideoUrl}
      />

      {/* Enhanced Animated Background */}
      <div className="compliance-background-animation">
        {/* <div className="compliance-floating-shape compliance-shape-1"></div>
        <div className="compliance-floating-shape compliance-shape-2"></div>
        <div className="compliance-floating-shape compliance-shape-3"></div>
        <div className="compliance-floating-shape compliance-shape-4"></div>
        <div className="compliance-floating-shape compliance-shape-5"></div> */}
        
        {/* Animated Gradient Orbs */}
        {/* <div className="compliance-gradient-orb compliance-orb-1"></div>
        <div className="compliance-gradient-orb compliance-orb-2"></div>
        <div className="compliance-gradient-orb compliance-orb-3"></div> */}
        
        {/* SVG Background Animation */}
        <svg className="compliance-bg-svg" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <linearGradient id="compliance-gradient1" x1="0%" y1="0%" x2="100%" y2="100%">
              <stop offset="0%" stopColor="rgba(16, 185, 129, 0.08)" />
              <stop offset="50%" stopColor="rgba(59, 130, 246, 0.05)" />
              <stop offset="100%" stopColor="rgba(139, 92, 246, 0.08)" />
            </linearGradient>
            <linearGradient id="compliance-gradient2" x1="100%" y1="0%" x2="0%" y2="100%">
              <stop offset="0%" stopColor="rgba(59, 130, 246, 0.06)" />
              <stop offset="50%" stopColor="rgba(16, 185, 129, 0.04)" />
              <stop offset="100%" stopColor="rgba(139, 92, 246, 0.06)" />
            </linearGradient>
            <pattern id="compliance-grid" width="10" height="10" patternUnits="userSpaceOnUse">
              <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(255,255,255,0.05)" strokeWidth="0.5"/>
            </pattern>
          </defs>
          <rect width="100" height="100" fill="url(#compliance-grid)" />
          <path 
            d="M0,15 Q30,5 60,25 T100,35 L100,100 L0,100 Z" 
            fill="url(#compliance-gradient1)"
            className="compliance-svg-path-1"
          />
          <path 
            d="M0,65 Q40,45 80,70 T100,85 L100,100 L0,100 Z" 
            fill="url(#compliance-gradient2)"
            opacity="0.8"
            className="compliance-svg-path-2"
          />
        </svg>
      </div>

<ComplainceHero/>
<ComplianceWiseCode/>
<HRCopilotFeatures/>
<HRCopilotNewsSection/>

    </div>
  );
};

export default Compliance;